/**
 * /ap/intake — AI-driven AP-invoice intake queue.
 *
 * Drag a PDF onto the form (or paste an email body), the server
 * parses it via the OCR router (PaddleOCR-VL → Claude vision
 * fallback), stores it in ``ap_invoice_intake``, and shows it in
 * the queue below for review.
 *
 * Per MONEY-AP-INTAKE-001 §5.1 (Channel P0 — admin upload/paste).
 * Backend module: ``app/modules/money/ap/intake/``.
 *
 * Two stages on this page:
 *   1. **Add invoice** — top panel with two input modes
 *      (drag-drop file / paste text). Posts to /api/ap/intake.
 *   2. **Review queue** — bottom table of awaiting-review rows.
 *      Click a row → expand to show parsed fields + Promote /
 *      Reject buttons.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@mui/material";
import { $fetch } from "ofetch";

import { api, errorDetail, errorMessage, getToken, setToken } from "@/lib/api";
import {
  accountsListQueryOptions,
  jobCostTypesQueryOptions,
  partyDetailQueryOptions,
} from "@/lib/queries";
import type { CostCode, Party, ProjectStatusStr, ProjectSummary } from "@/lib/types";
import { PartySelect } from "@/components/PartySelect";
import { useToast } from "@/components/Toaster";
import { FileDropZone } from "@/components/FileDropZone";
import { ProjectSelect } from "@/components/ProjectSelect";
import { CostCodeSelect } from "@/components/CostCodeSelect";
import { codeAndName } from "../lib/displayCode";

// Bill coding must be able to find the same normal projects shown on the
// Projects page. ProjectSelect otherwise defaults to active-only, and this
// screen intentionally hides its status control.
const BILL_PROJECT_STATUSES: ProjectStatusStr[] = [
  "bidding",
  "awarded",
  "active",
  "hold",
  "closed",
  "cancelled",
];

// ── Types (mirror the backend Pydantic shapes) ────────────────────────────

interface ParsedLine {
  description: string;
  amount: string;
  quantity?: string | null;
  unit?: string | null;
  unit_price?: string | null;
  /** GL account code the parser suggests for this line (AI assist). */
  suggested_account_code?: string | null;
}

interface ParsedInvoice {
  vendor_name?: string | null;
  vendor_email?: string | null;
  vendor_tax_id?: string | null;
  invoice_no?: string | null;
  invoice_date?: string | null;
  due_date?: string | null;
  payment_terms?: string | null;
  po_number?: string | null;
  /** Job / project reference exactly as printed on the bill. */
  job_reference?: string | null;
  currency: string;
  subtotal: string;
  tax_total: string;
  total: string;
  lines: ParsedLine[];
  field_confidence?: Record<string, string>;
  warnings?: { field: string; severity: string; msg: string }[];
}

interface ParseWarning {
  field: string;
  severity: string;
  msg: string;
  /** possible_duplicate: the other record ("intake" | "bill") and its id. */
  ref_kind?: string;
  ref_id?: string;
  /** vendor_candidates: every vendor record that could be the bill's vendor. */
  candidates?: VendorCandidate[];
}

interface VendorCandidate {
  id: string;
  name: string;
  /** Shown code, or null when the stored code is internal. */
  code?: string | null;
}

/** The vendor records the matcher said could be this bill's vendor (FIX-LOG 116). */
function vendorCandidates(row: { parse_warnings?: ParseWarning[] | null }): VendorCandidate[] {
  return (row.parse_warnings ?? []).find((w) => w.field === "vendor_candidates")?.candidates ?? [];
}

interface APIntakeRow {
  id: string;
  entity_id: string;
  channel: string;
  status: string;
  source_file_original_name?: string | null;
  source_file_size_bytes?: number | null;
  source_file_content_type?: string | null;
  source_text?: string | null;
  source_metadata?: {
    from?: string;
    subject?: string;
    to?: string;
    source?: string;
    links?: string[];
  } | null;
  parse_method?: string | null;
  parse_confidence?: string | null;
  parsed_payload?: ParsedInvoice | null;
  parse_warnings?: ParseWarning[] | null;
  matched_vendor_id?: string | null;
  match_confidence?: string | null;
  match_reason?: string | null;
  /** POST only: the upload was a file already filed; this is that row. */
  already_filed?: boolean;
  project_id?: string | null;
  /** Denormalized on read — so the queue can show the job without loading 1000 projects. */
  project_code?: string | null;
  project_name?: string | null;
  promoted_invoice_id?: string | null;
  rejected_reason?: string | null;
  auto_posted: boolean;
  autopilot_blocks?: string[] | null;
  created_at: string;
}

interface APIntakeListOut {
  items: APIntakeRow[];
  total: number;
}

// ── Autopilot block code → human label + resolution hint ─────────────────

const AUTOPILOT_BLOCK_LABELS: Record<string, { label: string; hint: string }> = {
  // Universal blocks
  no_preference_row:       { label: "Autopilot not configured", hint: "Open the vendor's Autopilot tab and set a confidence threshold." },
  no_threshold:            { label: "No threshold set", hint: "Set a minimum confidence score on the vendor's Autopilot tab." },
  confidence_missing:      { label: "Confidence score missing", hint: "The invoice couldn't be fully parsed or matched. Review manually." },
  new_vendor:              { label: "New vendor", hint: "First bill from a new vendor always requires manual review." },
  fx_currency:             { label: "Foreign currency", hint: "Non-USD invoices are not supported for auto-post in v1." },
  below_global_floor:      { label: "Confidence below 0.80 floor", hint: "Parse or match quality too low. Improve source document quality." },
  below_vendor_threshold:  { label: "Below vendor threshold", hint: "Combined confidence didn't clear the vendor's configured threshold." },
  over_ceiling:            { label: "Above dollar ceiling", hint: "Invoice total exceeds the per-vendor auto-post ceiling. Review manually." },
  watch_not_reconciled:    { label: "Spend watch mismatch", hint: "Bill amount doesn't reconcile with the vendor-watch MTD projection (>5% variance)." },
  // Construction P2 blocks
  po_required:             { label: "No open PO", hint: "Create or approve a PO for this vendor, or enable 'Allow PO-less' on the Autopilot tab for GL-only recurring charges." },
  po_tolerance_exceeded:   { label: "Over PO balance", hint: "Invoice exceeds the PO remaining balance. Create a change order or adjust the PO before approving." },
  no_goods_receipt:        { label: "No goods receipt", hint: "Post a receipt (POReceipt) for this PO in the Purchase Orders module to complete the 3-way match." },
  expired_coi:             { label: "COI / qualification expired", hint: "Renew the vendor's certificate of insurance in Vendor Qualifications before auto-posting resumes." },
  compliance_gate_fail:    { label: "Compliance alert open", hint: "Resolve the open critical compliance alert for this vendor (union, certified payroll, or Davis-Bacon) before posting." },
  retention_line:          { label: "Retention line", hint: "Invoices with retention require manual review — retention release is a project-level judgment call." },
  sub_pay_app:             { label: "Sub pay application", hint: "Schedule-of-values draws always require manual review." },
  possible_duplicate:      { label: "Possible duplicate", hint: "Another intake row or bill has the same vendor and invoice no. Check it before posting." },
  vendor_match_unconfirmed: { label: "Vendor match needs a look", hint: "The AI matched this vendor from the vendor records; confirm the vendor before it can auto-post." },
  lien_waiver_outstanding: { label: "Outstanding lien waiver", hint: "A prior lien waiver request from this vendor is unresolved. Mark it received/verified before auto-posting resumes." },
};

// ── Route declaration ─────────────────────────────────────────────────────

export const Route = createFileRoute("/_auth/bills-intake")({
  component: APIntakePage,
  validateSearch: (search: Record<string, unknown>): { focus?: string } => ({
    focus: typeof search.focus === "string" ? search.focus : undefined,
  }),
});

// ── Helpers ────────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  received:        { bg: "bg-slate-200",  text: "text-slate-800",  label: "Received" },
  parsing:         { bg: "bg-blue-100",   text: "text-blue-800",   label: "Parsing…" },
  parsed:          { bg: "bg-emerald-100",text: "text-emerald-800",label: "Parsed" },
  parse_failed:    { bg: "bg-rose-100",   text: "text-rose-800",   label: "Parse failed" },
  awaiting_review: { bg: "bg-amber-100",  text: "text-amber-800",  label: "Awaiting review" },
  promoted:        { bg: "bg-emerald-200",text: "text-emerald-900",label: "Promoted" },
  rejected:        { bg: "bg-slate-300",  text: "text-slate-800",  label: "Rejected" },
};

function formatMoney(amt: string | null | undefined, ccy = "USD"): string {
  if (!amt) return "";
  const n = Number(amt);
  if (Number.isNaN(n)) return amt;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: ccy,
    maximumFractionDigits: 2,
  }).format(n);
}

/** Same rules as the backend list filters — keep chips honest. */
const OPEN_STATUSES = new Set([
  "received", "parsing", "parsed", "awaiting_review", "parse_failed",
]);
const CRITICAL_SOON_DAYS = 3;
const CRITICAL_AMOUNT = 5000;

function parseYmd(s: string | null | undefined): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function utcToday(): Date {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
}

function daysUntil(due: Date, today: Date): number {
  return Math.round((due.getTime() - today.getTime()) / 86_400_000);
}

type UrgencyKey = "overdue" | "critical" | "blocking";

function urgencyFlags(row: APIntakeRow): UrgencyKey[] {
  if (!OPEN_STATUSES.has(row.status)) return [];
  const out: UrgencyKey[] = [];
  const due = parseYmd(row.parsed_payload?.due_date ?? null);
  const total = Number(row.parsed_payload?.total ?? 0);
  const today = utcToday();
  const overdue = !!(due && due < today);
  if (overdue) out.push("overdue");
  const dueSoonBig =
    !!(due && !overdue && daysUntil(due, today) <= CRITICAL_SOON_DAYS && total >= CRITICAL_AMOUNT);
  if (overdue || dueSoonBig || row.status === "parse_failed") out.push("critical");
  if (row.project_id) out.push("blocking");
  return out;
}

const URGENCY_CHIP: Record<UrgencyKey, { label: string; className: string; title: string }> = {
  overdue: {
    label: "Overdue",
    className: "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300",
    title: "Parsed due date is before today",
  },
  critical: {
    label: "Critical",
    className: "bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300",
    title: "Overdue, due within 3 days and ≥ $5,000, or parse failed",
  },
  blocking: {
    label: "Blocking job",
    className: "bg-violet-100 text-violet-800 dark:bg-violet-900/50 dark:text-violet-300",
    title: "Assigned to a project and still open — job cost can't clear until this posts or rejects",
  },
};

// ── Top-level page ────────────────────────────────────────────────────────

// Status filter chips. "Needs review" is the working set — everything a
// human must act on. Filters are server-side so the queue stays usable at
// thousands of rows across hundreds of projects.
const QUEUE_FILTERS: { key: string; label: string; statuses: string }[] = [
  { key: "review", label: "Needs review", statuses: "awaiting_review,parsed" },
  { key: "received", label: "Received", statuses: "received,parsing" },
  { key: "failed", label: "Failed", statuses: "parse_failed" },
  { key: "promoted", label: "Promoted", statuses: "promoted" },
  { key: "rejected", label: "Rejected", statuses: "rejected" },
  { key: "all", label: "All", statuses: "" },
];

const URGENCY_FILTERS: { key: "" | UrgencyKey; label: string }[] = [
  { key: "", label: "Any urgency" },
  { key: "overdue", label: "Overdue" },
  { key: "critical", label: "Critical" },
  { key: "blocking", label: "Blocking a job" },
];

const PAGE_SIZE = 50;

function APIntakePage() {
  const { focus } = Route.useSearch();
  const qc = useQueryClient();
  const toast = useToast();

  // Deep links (dashboard "bills waiting" action, nav badge) land on the
  // working set; a focused row may be in any status, so fall back to All.
  const [filterKey, setFilterKey] = useState(focus ? "all" : "review");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  // "" | "unassigned" | uuid — typeahead sets the uuid; "Unassigned" chip sets the sentinel.
  const [projectFilter, setProjectFilter] = useState("");
  const [filterProject, setFilterProject] = useState<ProjectSummary | null>(null);
  const [urgencyFilter, setUrgencyFilter] = useState<"" | UrgencyKey>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Debounce the search box so we don't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filter = QUEUE_FILTERS.find((f) => f.key === filterKey) ?? QUEUE_FILTERS[0];
  const listParams = new URLSearchParams();
  if (filter.statuses) listParams.set("status", filter.statuses);
  if (search) listParams.set("q", search);
  if (projectFilter) listParams.set("project_id", projectFilter);
  if (urgencyFilter) listParams.set("urgency", urgencyFilter);
  if (dateFrom) listParams.set("from", dateFrom);
  if (dateTo) listParams.set("to", dateTo);
  listParams.set("limit", String(PAGE_SIZE));
  listParams.set("offset", String(offset));

  const intakeList = useQuery({
    queryKey: [
      "ap-intake", "list", filter.statuses, search, offset,
      projectFilter, urgencyFilter, dateFrom, dateTo,
    ],
    queryFn: () => api<APIntakeListOut>(`/api/ap/intake?${listParams.toString()}`),
    refetchInterval: 5000, // Poll while parses are in flight
    placeholderData: (prev) => prev, // keep rows while a filter/page loads
  });
  const accountsQ = useQuery(accountsListQueryOptions());

  const total = intakeList.data?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  // Uploader starts collapsed — the queue is the page. Auto-open when the
  // queue is truly empty (first-run) so new users still see how bills get in.
  const [addOpen, setAddOpen] = useState(false);
  const showAdd = addOpen || (
    intakeList.isSuccess && total === 0 && filterKey === "review"
    && !search && !projectFilter && !urgencyFilter && !dateFrom && !dateTo
  );

  const receivedCount =
    intakeList.data?.items.filter((r) => r.status === "received").length ?? 0;

  // ── Selection (master-detail) ──────────────────────────────────────────
  // The queue is an inbox: list on the left, one bill's full review on the
  // right. Deep links (?focus=) pre-select; otherwise the first row is
  // selected so the reviewer always lands on work, not on an empty pane.
  const items = intakeList.data?.items ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(focus ?? null);
  const selected = items.find((r) => r.id === selectedId) ?? null;
  useEffect(() => {
    if (!selected && items.length > 0) setSelectedId(items[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((r) => r.id).join(","), selectedId]);

  /** Select the neighbor of the current bill (keyboard + post-action advance). */
  const selectNeighbor = (dir: 1 | -1) => {
    if (items.length === 0) return;
    const idx = items.findIndex((r) => r.id === selectedId);
    const next = items[Math.min(items.length - 1, Math.max(0, idx + dir))];
    if (next) setSelectedId(next.id);
  };

  /** After promote/reject, move to the next bill so review flows like an inbox. */
  const advanceAfterAction = () => {
    const idx = items.findIndex((r) => r.id === selectedId);
    const remaining = items.filter((_, i) => i !== idx);
    setSelectedId(remaining[Math.min(idx, remaining.length - 1)]?.id ?? null);
  };

  // ↑/↓ move through the list unless the user is typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "ArrowDown") { e.preventDefault(); selectNeighbor(1); }
      if (e.key === "ArrowUp") { e.preventDefault(); selectNeighbor(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const parseReceived = useMutation({
    mutationFn: () =>
      api<APIntakeListOut>("/api/ap/intake/parse-received?limit=25", {
        method: "POST",
      }),
    onSuccess: (out) => {
      void qc.invalidateQueries({ queryKey: ["ap-intake"] });
      const ok = out.items.filter((r) => r.status === "awaiting_review").length;
      const failed = out.items.filter((r) => r.status === "parse_failed").length;
      toast.success(
        `Parsed ${out.total} received bill${out.total === 1 ? "" : "s"}` +
          (ok || failed ? ` → ${ok} ready, ${failed} failed` : ""),
      );
    },
    onError: (err) => {
      const raw = (err as { data?: unknown })?.data ?? err;
      const detail =
        typeof raw === "string" ? raw : JSON.stringify(raw).slice(0, 200);
      toast.error(`Parse received failed: ${detail}`);
    },
  });

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* ── Toolbar: everything in one quiet row ── */}
      <div className="bg-white border-b border-slate-200 px-4 py-2 dark:bg-gray-900 dark:border-gray-800 flex items-center gap-2 flex-wrap flex-shrink-0">
        <h1 className="text-base font-semibold text-slate-900 dark:text-slate-100 mr-1">
          Bill intake
        </h1>

        <div className="flex items-center gap-1 bg-slate-100 dark:bg-gray-800 rounded-lg p-0.5">
          {QUEUE_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => {
                setFilterKey(f.key);
                setOffset(0);
              }}
              className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-all ${
                filterKey === f.key
                  ? "bg-white dark:bg-gray-700 shadow-sm text-slate-900 dark:text-slate-100"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search anything…"
          title="Full-text search: vendor, invoice #, line items, email text"
          className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 w-44"
        />

        <div className="flex items-center gap-1.5 min-w-0" title="Search by job code or name — works with 1000+ projects">
          <div className="w-[220px]">
            <ProjectSelect
              value={filterProject}
              onChange={(p) => {
                setFilterProject(p);
                setProjectFilter(p?.id ?? "");
                setOffset(0);
              }}
              statusFilter={BILL_PROJECT_STATUSES}
              showStatusDropdown={false}
              label=""
              placeholder="All projects…"
              size="small"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setFilterProject(null);
              setProjectFilter(projectFilter === "unassigned" ? "" : "unassigned");
              setOffset(0);
            }}
            className={`px-2 py-1.5 rounded-lg border text-xs font-medium whitespace-nowrap ${
              projectFilter === "unassigned"
                ? "border-violet-300 bg-violet-50 text-violet-800 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-200"
                : "border-slate-200 dark:border-gray-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800"
            }`}
          >
            Unassigned
          </button>
        </div>

        <select
          value={urgencyFilter}
          onChange={(e) => {
            setUrgencyFilter(e.target.value as "" | UrgencyKey);
            setOffset(0);
          }}
          title="Overdue = past due date · Critical = overdue / due soon & large / parse failed · Blocking = assigned to a job and still open"
          className="px-2 py-1.5 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-slate-900 dark:text-slate-100"
        >
          {URGENCY_FILTERS.map((u) => (
            <option key={u.key || "any"} value={u.key}>{u.label}</option>
          ))}
        </select>

        <div className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setOffset(0); }}
            title="Received from"
            className="px-2 py-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-slate-900 dark:text-slate-100"
          />
          <span>–</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setOffset(0); }}
            title="Received to"
            className="px-2 py-1 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-slate-900 dark:text-slate-100"
          />
        </div>

        {receivedCount > 0 && (
          <Button
            size="small"
            variant="outlined"
            disabled={parseReceived.isPending}
            onClick={() => parseReceived.mutate()}
          >
            {parseReceived.isPending
              ? "Parsing…"
              : `Parse ${receivedCount} received`}
          </Button>
        )}

        <div className="flex-1" />

        <button
          onClick={() => setAddOpen(!addOpen)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
        >
          <span className="text-base leading-none">＋</span> Add bills
        </button>
      </div>

      {/* Add-bills panel drops below the toolbar when open (or first run) */}
      {showAdd && (
        <div className="border-b border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-950 px-4 py-3 flex-shrink-0">
          <AddIntakePanel />
        </div>
      )}

      {/* ── Master-detail: list left, review pane right ── */}
      <div className="flex-1 flex min-h-0">
        {/* List */}
        <div
          className={`${selected ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[360px] lg:flex-shrink-0 border-r border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900`}
        >
          <div className="flex-1 overflow-y-auto">
            {intakeList.isLoading ? (
              <div className="p-4 text-sm text-slate-500">Loading queue…</div>
            ) : items.length > 0 ? (
              items.map((row) => (
                <QueueListItem
                  key={row.id}
                  row={row}
                  selected={row.id === selectedId}
                  onSelect={() => setSelectedId(row.id)}
                  projectName={
                    row.project_name
                      ? (row.project_code
                          ? `${row.project_code} · ${row.project_name}`
                          : row.project_name)
                      : undefined
                  }
                />
              ))
            ) : (
              <div className="p-4 text-sm text-slate-500 italic">
                {search || filterKey !== "review"
                  ? "Nothing matches this filter."
                  : "Queue is clear 🎉 — forward a bill to your intake email or add one."}
              </div>
            )}
          </div>

          {/* List footer: count + paging + keyboard hint */}
          <div className="border-t border-slate-200 dark:border-gray-800 px-3 py-1.5 flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 flex-shrink-0">
            <span>{total === 0 ? "0" : `${pageStart}–${pageEnd} of ${total}`}</span>
            {total > PAGE_SIZE && (
              <>
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="px-1.5 py-0.5 rounded border border-slate-200 dark:border-gray-700 disabled:opacity-40"
                >
                  ←
                </button>
                <button
                  disabled={pageEnd >= total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  className="px-1.5 py-0.5 rounded border border-slate-200 dark:border-gray-700 disabled:opacity-40"
                >
                  →
                </button>
              </>
            )}
            <span className="flex-1" />
            <span className="hidden lg:inline">↑↓ to move</span>
          </div>
        </div>

        {/* Detail pane */}
        <div className={`${selected ? "flex" : "hidden lg:flex"} flex-col flex-1 min-w-0 bg-slate-50 dark:bg-gray-950`}>
          {selected ? (
            <BillDetailPane
              key={selected.id}
              row={selected}
              accounts={accountsQ.data ?? []}
              onBack={() => setSelectedId(null)}
              onDone={advanceAfterAction}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400 dark:text-slate-500">
              {items.length > 0
                ? "Select a bill to review it"
                : "Bills you forward or upload land here for review"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Queue list item (left pane) ───────────────────────────────────────────

function QueueListItem({
  row,
  selected,
  onSelect,
  projectName,
}: {
  row: APIntakeRow;
  selected: boolean;
  onSelect: () => void;
  projectName?: string;
}) {
  const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.received;
  const parsed = row.parsed_payload;
  const flags = urgencyFlags(row);
  const title =
    parsed?.vendor_name ||
    row.source_file_original_name ||
    row.source_metadata?.subject ||
    (row.source_text ? row.source_text.slice(0, 40) + "…" : "(unidentified)");
  const sub =
    projectName ||
    row.source_metadata?.from ||
    row.source_file_original_name ||
    (row.channel === "paste_email" ? "pasted email" : row.channel);
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 border-b border-slate-100 dark:border-gray-800 transition-colors ${
        selected
          ? "bg-blue-50 dark:bg-blue-950/40 border-l-2 border-l-blue-600"
          : "hover:bg-slate-50 dark:hover:bg-gray-800 border-l-2 border-l-transparent"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate flex-1">
          {title}
        </span>
        {parsed?.total && (
          <span className="text-sm font-mono text-slate-700 dark:text-slate-300 flex-shrink-0">
            {formatMoney(parsed.total, parsed.currency)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
        <span className={`px-1.5 py-px rounded text-[10px] font-medium ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
        {flags.map((f) => (
          <span
            key={f}
            title={URGENCY_CHIP[f].title}
            className={`px-1.5 py-px rounded text-[10px] font-medium ${URGENCY_CHIP[f].className}`}
          >
            {URGENCY_CHIP[f].label}
          </span>
        ))}
        <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate flex-1 min-w-0">
          {sub}
        </span>
        <span className="text-[11px] text-slate-400 dark:text-slate-500 flex-shrink-0">
          {parsed?.due_date
            ? `due ${parsed.due_date}`
            : new Date(row.created_at).toLocaleDateString()}
        </span>
      </div>
    </button>
  );
}

// ── Add-invoice panel (drag-drop + paste tabs) ────────────────────────────

/** Append new files to a pending batch, ignoring exact re-drops. */
function mergeFiles(prev: File[], incoming: File[]): File[] {
  const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
  return [...prev, ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`))];
}

function AddIntakePanel() {
  const [mode, setMode] = useState<"file" | "paste">("file");
  // A batch: suppliers send a stack of bills at once, so the queue accepts a
  // whole drop rather than making the user repeat the flow per document.
  const [files, setFiles] = useState<File[]>([]);
  const [pasteText, setPasteText] = useState("");
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const file = files[0] ?? null;

  const qc = useQueryClient();
  const toast = useToast();

  const submitOne = async (body: FormData): Promise<APIntakeRow> => {
    const token = getToken();
    // Use raw $fetch so we don't get a JSON content-type header.
    return $fetch("/api/ap/intake", {
      method: "POST",
      body,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
  };

  const create = useMutation({
    mutationFn: async (): Promise<{ rows: APIntakeRow[]; failed: string[] }> => {
      if (mode === "paste") {
        if (!pasteText.trim()) throw new Error("Paste an email body first");
        const fd = new FormData();
        fd.append("paste_text", pasteText);
        return { rows: [await submitOne(fd)], failed: [] };
      }
      if (!files.length) throw new Error("Pick a file first");

      // Sequential, not parallel: each upload runs the parse + vendor-match +
      // autopilot pipeline server-side, and firing a stack of them at once
      // would contend for the same OCR path. One bad document also must not
      // sink the rest of the batch, so failures are collected, not thrown.
      const rows: APIntakeRow[] = [];
      const failed: string[] = [];
      setProgress({ done: 0, total: files.length });
      for (const [i, f] of files.entries()) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          rows.push(await submitOne(fd));
        } catch (err) {
          // Raw $fetch bypasses api()'s 401 handling, so an expired session
          // mid-batch would look like "this document is unreadable" for every
          // remaining file. Stop and send them to sign in instead.
          if ((err as { status?: number; response?: { status?: number } })?.status === 401
            || (err as { response?: { status?: number } })?.response?.status === 401) {
            setToken(null);
            window.location.assign("/login");
            throw err;
          }
          failed.push(f.name);
        }
        setProgress({ done: i + 1, total: files.length });
      }
      return { rows, failed };
    },
    onSuccess: ({ rows, failed }) => {
      qc.invalidateQueries({ queryKey: ["ap-intake", "list"] });
      if (rows.length === 1 && !failed.length) {
        const status = STATUS_BADGE[rows[0].status]?.label ?? rows[0].status;
        toast.success(
          rows[0].already_filed
            ? `Already filed (same file) → ${status}`
            : `Intake created → ${status}`,
        );
      } else if (rows.length) {
        toast.success(`${rows.length} bill${rows.length === 1 ? "" : "s"} added to the queue`);
      }
      if (failed.length) {
        toast.error(`Couldn't read ${failed.length}: ${failed.slice(0, 3).join(", ")}`);
      }
      setFiles([]);
      setPasteText("");
      setProgress(null);
    },
    onError: (err: Error) => {
      setProgress(null);
      toast.error(`Submit failed: ${err.message}`);
    },
  });

  return (
    <div className="border border-slate-200 dark:border-gray-800 rounded-md bg-white dark:bg-gray-900 overflow-hidden">
      {/* Tab strip */}
      <div className="flex border-b border-slate-200 dark:border-gray-800">
        <button
          onClick={() => setMode("file")}
          className={`px-4 py-2 text-sm font-medium transition ${
            mode === "file"
              ? "border-b-2 border-amber-500 text-slate-900 dark:text-amber-400"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Drop a file
        </button>
        <button
          onClick={() => setMode("paste")}
          className={`px-4 py-2 text-sm font-medium transition ${
            mode === "paste"
              ? "border-b-2 border-amber-500 text-slate-900 dark:text-amber-400"
              : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          Paste email
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        {mode === "file" ? (
          <FileDropZone
            file={file}
            files={files}
            // Append: dropping 3 bills then 2 more should queue 5, not discard
            // the first 3. De-duped by name+size so a re-drop isn't doubled.
            onFile={(f) => setFiles((prev) => mergeFiles(prev, [f]))}
            onFiles={(fs) => setFiles((prev) => mergeFiles(prev, fs))}
            onClear={() => setFiles([])}
            label={
              files.length > 1
                ? `${files.length} bills ready to upload`
                : "Drop bills here — one or many"
            }
            sublabel="Accepts PDF · JPG · PNG · WEBP · HEIC · TIFF"
            enablePaste
            disabled={create.isPending}
          />
        ) : (
          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            rows={8}
            placeholder={`From: billing@anthropic.com\nSubject: Your invoice\n\nTotal: $47.20\nDate: 2026-04-15\n…`}
            className="w-full text-sm font-mono border border-slate-300 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          />
        )}

        {create.isPending && (
          <div className="mt-3 text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded px-3 py-2 dark:text-slate-300 dark:bg-blue-950 dark:border-blue-900">
            {progress && progress.total > 1
              ? `Parsing ${Math.min(progress.done + 1, progress.total)} of ${progress.total} — 5–30 seconds each.`
              : "Parsing — typically 5–30 seconds depending on the document."}
          </div>
        )}

        <div className="mt-3 flex justify-end">
          <Button
            variant="contained"
            size="small"
            onClick={() => create.mutate()}
            disabled={
              create.isPending ||
              (mode === "file" ? !files.length : !pasteText.trim())
            }
          >
            {create.isPending
              ? "Sending…"
              : files.length > 1
                ? `Submit ${files.length} + parse`
                : "Submit + parse"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── One row in the queue (collapsed → expanded) ────────────────────────────

interface AccountSummary {
  id: string;
  code: string;
  name: string;
  account_type?: string;
}
interface PartySummary {
  id: string;
  code: string;
  name: string;
  party_type: string;
}

/** Confidence chip — the AI's self-assessment, colored so a reviewer can
 *  triage at a glance: green = trust it, amber = skim it, red = read it. */
function ConfidenceChip({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  const tone =
    pct >= 85
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300"
      : pct >= 60
        ? "bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
        : "bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-300";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>
      {label} {pct}%
    </span>
  );
}

/** Build a ProjectSelect value from denormalized intake fields (no bulk project fetch). */
function projectFromIntake(row: APIntakeRow): ProjectSummary | null {
  if (!row.project_id) return null;
  return {
    id: row.project_id,
    code: row.project_code ?? "",
    name: row.project_name ?? "Project",
    status: "active",
    customer_id: null,
    currency_code: "USD",
  };
}

function BillDetailPane({
  row,
  accounts,
  onBack,
  onDone,
}: {
  row: APIntakeRow;
  accounts: AccountSummary[];
  /** Mobile master-detail: return to the list. */
  onBack: () => void;
  /** Called after promote/reject so the parent advances to the next bill. */
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const parsed = row.parsed_payload;
  // The matched vendor is read by id, not looked up in the loaded party list
  // (which need not hold it), so the match always shows.
  const matchedVendorQ = useQuery(partyDetailQueryOptions(row.matched_vendor_id ?? ""));
  const matchedVendor: PartySummary | undefined = row.matched_vendor_id
    ? matchedVendorQ.data
    : undefined;
  const flags = urgencyFlags(row);
  const assignedProject = projectFromIntake(row);
  const title =
    parsed?.vendor_name ||
    row.source_file_original_name ||
    row.source_metadata?.subject ||
    "(unidentified)";

  // The parser tags each line with a suggested GL account; surface the first
  // one so the reviewer's account picker starts where the AI landed.
  const suggestedAccountCode =
    parsed?.lines?.find((l) => l.suggested_account_code)?.suggested_account_code ?? null;

  const parseNow = useMutation({
    mutationFn: () =>
      api<APIntakeRow>(`/api/ap/intake/${row.id}/parse`, { method: "POST" }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ["ap-intake"] });
      const label = STATUS_BADGE[updated.status]?.label ?? updated.status;
      toast.success(`Parse finished → ${label}`);
    },
    onError: (err) => {
      const raw = (err as { data?: unknown })?.data ?? err;
      const detail =
        typeof raw === "string" ? raw : JSON.stringify(raw).slice(0, 200);
      toast.error(`Parse failed: ${detail}`);
    },
  });

  const assignProject = useMutation({
    mutationFn: (project_id: string | null) =>
      api<APIntakeRow>(`/api/ap/intake/${row.id}/assign`, {
        method: "PATCH",
        body: { project_id },
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ["ap-intake"] });
      const label = updated.project_name
        || (updated.project_code ? updated.project_code : null)
        || "project";
      toast.success(
        updated.project_id ? `Assigned to ${label}` : "Project cleared",
      );
    },
    onError: (err) => {
      const raw = (err as { data?: unknown })?.data ?? err;
      const detail =
        typeof raw === "string" ? raw : JSON.stringify(raw).slice(0, 200);
      toast.error(`Assign failed: ${detail}`);
    },
  });

  // Hand the bill to the CFO agent with full context — the reviewer's
  // "second opinion" button. The agent can check for duplicates, explain
  // the vendor match, and recommend a GL account.
  const askAi = () => {
    const projectName = row.project_name
      || (row.project_code ? row.project_code : null);
    const summary = [
      `vendor "${parsed?.vendor_name ?? "unknown"}"`,
      parsed?.invoice_no ? `invoice #${parsed.invoice_no}` : null,
      parsed?.total ? `total ${formatMoney(parsed.total, parsed.currency)}` : null,
      parsed?.due_date ? `due ${parsed.due_date}` : null,
      projectName ? `project ${projectName}` : null,
      `status ${row.status}`,
      row.source_metadata?.from ? `emailed from ${row.source_metadata.from}` : null,
    ].filter(Boolean).join(", ");
    window.dispatchEvent(
      new CustomEvent("bynaus:open-agent", {
        detail: {
          prompt:
            `I'm reviewing a bill in the intake queue (${summary}). ` +
            `Is this a duplicate? Does the amount look right versus this vendor's history? ` +
            `Which GL expense account should it post to?` +
            (projectName ? ` Does it belong on project ${projectName}?` : ""),
          autoSend: true,
        },
      }),
    );
  };

  const badge = STATUS_BADGE[row.status] ?? STATUS_BADGE.received;
  const due = parseYmd(parsed?.due_date ?? null);
  const dueLabel = due
    ? flags.includes("overdue")
      ? `Overdue · due ${parsed!.due_date}`
      : `Due ${parsed!.due_date}`
    : null;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* ── Bill header ── */}
      <div className="bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-5 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="lg:hidden text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm"
          >
            ← Back
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                {title}
              </h2>
              <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${badge.bg} ${badge.text}`}>
                {badge.label}
              </span>
              {flags.map((f) => (
                <span
                  key={f}
                  title={URGENCY_CHIP[f].title}
                  className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${URGENCY_CHIP[f].className}`}
                >
                  {URGENCY_CHIP[f].label}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {parsed?.total && (
                <span className="text-base font-mono font-semibold text-slate-900 dark:text-slate-100">
                  {formatMoney(parsed.total, parsed.currency)}
                </span>
              )}
              {parsed?.invoice_no && (
                <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">
                  #{parsed.invoice_no}
                </span>
              )}
              {dueLabel && (
                <span
                  className={`text-xs font-medium ${
                    flags.includes("overdue")
                      ? "text-rose-700 dark:text-rose-300"
                      : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {dueLabel}
                </span>
              )}
              <span className="text-xs text-slate-400 dark:text-slate-500">
                received {new Date(row.created_at).toLocaleString()}
              </span>
              <ConfidenceChip
                label="AI read"
                value={row.parse_confidence ? Number(row.parse_confidence) : null}
              />
              <ConfidenceChip
                label="vendor match"
                value={row.match_confidence ? Number(row.match_confidence) : null}
              />
            </div>
          </div>
          <button
            onClick={askAi}
            title="Ask the CFO agent: duplicate? right amount? which GL account?"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-900/50 bg-blue-50 dark:bg-blue-950/40 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors flex-shrink-0"
          >
            <span>✦</span> Ask AI
          </button>
        </div>
      </div>

      {/* ── Scrollable detail body — cap width so labels aren't miles from values ── */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="max-w-xl space-y-3">
          <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                Project / job
              </label>
              {row.project_id && OPEN_STATUSES.has(row.status) && (
                <span className="text-[11px] font-medium text-violet-600 dark:text-violet-400">
                  Blocking until posted
                </span>
              )}
            </div>
            <ProjectSelect
              value={assignedProject}
              onChange={(p) => assignProject.mutate(p?.id ?? null)}
              disabled={assignProject.isPending}
              statusFilter={BILL_PROJECT_STATUSES}
              showStatusDropdown={false}
              label=""
              placeholder="Search job code or name…"
              size="small"
            />
          </div>

          {row.status === "parse_failed" && row.parse_warnings && (
            <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-2 dark:bg-rose-950 dark:border-rose-900 dark:text-rose-300">
              <div className="font-medium mb-1">Parse failed</div>
              {row.parse_warnings.map((w, i) => (
                <div key={i} className="text-xs">
                  · {w.field}: {w.msg}
                </div>
              ))}
            </div>
          )}

          {row.status !== "parse_failed" &&
            row.parse_warnings?.some((w) => w.field === "possible_duplicate") && (
            <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-200">
              <div className="font-medium mb-1">Possible duplicate</div>
              {row.parse_warnings
                .filter((w) => w.field === "possible_duplicate")
                .map((w, i) => (
                  <div key={i} className="text-xs">
                    · {w.msg}{" "}
                    {w.ref_kind === "bill" && w.ref_id ? (
                      <a href={`/invoices/ap/${w.ref_id}`} className="underline">
                        Open bill
                      </a>
                    ) : w.ref_id ? (
                      <a href={`/bills-intake?focus=${w.ref_id}`} className="underline">
                        Open intake row
                      </a>
                    ) : null}
                  </div>
                ))}
            </div>
          )}

          {parsed && (
            <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-3 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-2">
                ✦ What the AI read
              </div>
              <ParsedFieldsTable
                parsed={parsed}
                matchedVendor={matchedVendor}
                candidateCount={vendorCandidates(row).length}
                matchReason={row.match_reason}
                matchConfidence={row.match_confidence}
                parseConfidence={row.parse_confidence}
              />
            </div>
          )}

          {/* Link-only email notices (AWS / SaaS) — show extracted bill links */}
          {row.source_metadata?.links && row.source_metadata.links.length > 0 && (
            <div className="text-sm space-y-1">
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Links from email
              </div>
              <ul className="space-y-1">
                {row.source_metadata.links.map((url) => (
                  <li key={url}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 underline break-all text-xs"
                    >
                      {url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Autopilot block explanation — shown when live mode blocked this bill */}
          {row.autopilot_blocks && row.autopilot_blocks.length > 0 && (
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2.5">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1.5">
                ⚡ Autopilot sent to review — {row.autopilot_blocks.length} block{row.autopilot_blocks.length !== 1 ? "s" : ""}
              </p>
              <ul className="space-y-1">
                {row.autopilot_blocks.map((code) => {
                  const info = AUTOPILOT_BLOCK_LABELS[code];
                  return (
                    <li key={code} className="text-xs">
                      <span className="font-medium text-amber-900 dark:text-amber-200">
                        {info ? info.label : code}
                      </span>
                      {info && (
                        <span className="text-amber-700 dark:text-amber-400 ml-1">
                          — {info.hint}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Action bar */}
          {(row.status === "received" || row.status === "parse_failed") && (
            <div className="flex items-center gap-3">
              <Button
                size="small"
                variant="contained"
                disabled={parseNow.isPending}
                onClick={() => parseNow.mutate()}
              >
                {parseNow.isPending ? "Parsing…" : "Parse now"}
              </Button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                Filed but not OCR&apos;d yet — run parse to extract vendor and amount.
              </span>
            </div>
          )}
          {row.status === "awaiting_review" || row.status === "parsed" ? (
            <PromoteRejectBar
              row={row}
              accounts={accounts}
              defaultVendorId={row.matched_vendor_id}
              suggestedAccountCode={suggestedAccountCode}
              onDone={onDone}
            />
          ) : row.status === "promoted" && row.promoted_invoice_id ? (
            <div className="flex items-center gap-2 flex-wrap text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 dark:bg-emerald-950 dark:border-emerald-900 dark:text-emerald-300">
              <span>✓ Promoted to AP invoice</span>
              <a
                href={`/invoices/ap/${row.promoted_invoice_id}`}
                className="underline"
              >
                open invoice
              </a>
              {row.auto_posted && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border border-blue-200 dark:border-blue-700">
                  ⚡ Auto-posted
                </span>
              )}
            </div>
          ) : row.status === "rejected" ? (
            <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-700 dark:text-slate-300">
              Rejected — {row.rejected_reason || "(no reason given)"}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ── Parsed-fields display ─────────────────────────────────────────────────

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-2 text-sm leading-5 py-0.5">
      <span className="w-[7.5rem] flex-shrink-0 text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <span className="min-w-0 text-slate-900 dark:text-slate-100">{children}</span>
    </div>
  );
}

function ParsedFieldsTable({
  parsed,
  matchedVendor,
  candidateCount = 0,
  matchReason,
  matchConfidence,
  parseConfidence,
}: {
  parsed: ParsedInvoice;
  matchedVendor?: PartySummary;
  /** Vendor records that could be this vendor when none is a single match. */
  candidateCount?: number;
  matchReason?: string | null;
  matchConfidence?: string | null;
  parseConfidence?: string | null;
}) {
  return (
    <div className="space-y-0.5">
      <FieldRow label="Vendor (parsed)">
        {parsed.vendor_name || <span className="italic text-slate-400">—</span>}
      </FieldRow>
      <FieldRow label="Vendor (matched)">
        {matchedVendor ? (
          <>
            {matchedVendor.name}
            <span className="ml-1.5 text-xs text-slate-500">
              ({matchReason} · {matchConfidence ? Number(matchConfidence) * 100 : 0}%)
            </span>
          </>
        ) : candidateCount > 1 ? (
          <span className="text-amber-700 dark:text-amber-300">
            {candidateCount} possible vendor records — choose one below
          </span>
        ) : (
          <span className="italic text-slate-400">no match</span>
        )}
      </FieldRow>
      <FieldRow label="Invoice no.">
        <span className="font-mono">
          {parsed.invoice_no || <span className="italic text-slate-400 font-sans">—</span>}
        </span>
      </FieldRow>
      <FieldRow label="Date">
        {parsed.invoice_date || <span className="italic text-slate-400">—</span>}
      </FieldRow>
      {parsed.due_date && (
        <FieldRow label="Due">{parsed.due_date}</FieldRow>
      )}
      {parsed.payment_terms && (
        <FieldRow label="Terms">{parsed.payment_terms.replace("net_", "Net ")}</FieldRow>
      )}
      {parsed.po_number && (
        <FieldRow label="PO no.">
          <span className="font-mono">{parsed.po_number}</span>
        </FieldRow>
      )}
      {parsed.job_reference && (
        <FieldRow label="Job (printed)">{parsed.job_reference}</FieldRow>
      )}
      <FieldRow label="Total">
        <span className="font-mono font-medium">
          {formatMoney(parsed.total, parsed.currency)}
        </span>
      </FieldRow>
      <FieldRow label="AI confidence">
        {parseConfidence
          ? `${(Number(parseConfidence) * 100).toFixed(0)}%`
          : "—"}
      </FieldRow>

      {parsed.lines && parsed.lines.length > 0 && (
        <div className="mt-2.5 pt-2 border-t border-slate-100 dark:border-gray-800">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">
            Line items
          </div>
          <ul className="space-y-1">
            {parsed.lines.map((l, i) => (
              <li key={i} className="flex gap-3 text-xs">
                <span className="flex-1 min-w-0 text-slate-900 dark:text-slate-100">
                  {l.description}
                  {l.quantity && l.unit_price && (
                    <span className="ml-1.5 font-mono text-slate-500 dark:text-slate-400">
                      {Number(l.quantity)} × {formatMoney(l.unit_price, parsed.currency)}
                    </span>
                  )}
                </span>
                <span className="flex-shrink-0 font-mono text-slate-700 dark:text-slate-300">
                  {formatMoney(l.amount, parsed.currency)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Promote / Reject action bar ────────────────────────────────────────────

function PromoteRejectBar({
  row,
  accounts,
  defaultVendorId,
  suggestedAccountCode,
  onDone,
}: {
  row: APIntakeRow;
  accounts: AccountSummary[];
  defaultVendorId?: string | null;
  /** GL account code the parser suggested from the line items. */
  suggestedAccountCode?: string | null;
  /** Notify the parent after promote/reject so it can advance the queue. */
  onDone?: () => void;
}) {
  // AI pre-fill: vendor from the match engine, GL account from the parser's
  // line-level suggestion. The reviewer confirms rather than hunts.
  const suggestedAccount = suggestedAccountCode
    ? accounts.find((a) => a.code === suggestedAccountCode)
    : undefined;
  const [vendorId, setVendorId] = useState<string>(defaultVendorId ?? "");
  const candidates = vendorCandidates(row);
  // Follow the match when it arrives or changes (a re-parse can match later).
  useEffect(() => {
    setVendorId(defaultVendorId ?? "");
  }, [defaultVendorId]);
  // The picker shows the chosen vendor resolved by id; a pick shows at once.
  const [pickedVendor, setPickedVendor] = useState<Party | null>(null);
  const vendorQ = useQuery({
    ...partyDetailQueryOptions(vendorId),
    enabled: Boolean(vendorId) && pickedVendor?.id !== vendorId,
  });
  const vendorValue = vendorId
    ? (pickedVendor?.id === vendorId ? pickedVendor : vendorQ.data ?? null)
    : null;
  const [accountId, setAccountId] = useState<string>(suggestedAccount?.id ?? "");
  // Job-cost coding. Only offered once the bill is assigned to a job — a cost
  // code without a project can't be posted to job cost, and the API refuses it.
  const [costCode, setCostCode] = useState<CostCode | null>(null);
  const [costTypeId, setCostTypeId] = useState<string>("");
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const qc = useQueryClient();
  const toast = useToast();
  const costTypesQuery = useQuery(jobCostTypesQueryOptions());
  const costTypeOptions = costTypesQuery.data ?? [];

  // The job is assigned on the intake row during review (PATCH /assign), so
  // the picker isn't asked twice. Clear coding if the job is cleared.
  const projectId = row.project_id ?? null;
  useEffect(() => {
    if (!projectId) {
      setCostCode(null);
      setCostTypeId("");
    }
  }, [projectId]);

  // The possible-duplicate warnings shown above this bar. Promoting while they
  // are shown acknowledges them; the API re-checks against the vendor picked
  // here and refuses (409) with any warning the reviewer has not seen yet.
  const shownDuplicateIds = (row.parse_warnings ?? [])
    .filter((w) => w.field === "possible_duplicate" && w.ref_id)
    .map((w) => w.ref_id as string);

  const promote = useMutation({
    mutationFn: async () => {
      return api(`/api/ap/intake/${row.id}/promote`, {
        method: "POST",
        body: {
          vendor_id: vendorId || undefined,
          account_id: accountId || undefined,
          cost_code_id: costCode?.id || undefined,
          cost_type_id: costTypeId || undefined,
          acknowledged_duplicate_ids: shownDuplicateIds,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ap-intake", "list"] });
      toast.success("Promoted to AP invoice (draft)");
      onDone?.();
    },
    onError: (err: Error) => {
      if (errorDetail(err)?.reason === "possible_duplicate") {
        // The warnings are now on the row: reload it so they show above.
        void qc.invalidateQueries({ queryKey: ["ap-intake"] });
        toast.info(errorMessage(err));
        return;
      }
      toast.error(`Promote failed: ${errorMessage(err)}`);
    },
  });

  const reject = useMutation({
    mutationFn: async () => {
      return api(`/api/ap/intake/${row.id}/reject`, {
        method: "POST",
        body: { reason: rejectReason },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ap-intake", "list"] });
      toast.success("Rejected");
      setRejecting(false);
      setRejectReason("");
      onDone?.();
    },
    onError: (err: Error) => toast.error(`Reject failed: ${err.message}`),
  });

  // Filter to expense / cogs accounts (5xxx + 6xxx by convention)
  const expenseAccounts = accounts.filter(
    (a) => a.code.startsWith("5") || a.code.startsWith("6"),
  );

  if (rejecting) {
    return (
      <div className="border border-rose-200 dark:border-rose-900 bg-rose-50 dark:bg-rose-950 rounded p-3 space-y-2">
        <div className="text-xs font-medium text-rose-800 dark:text-rose-300">
          Why are you rejecting this?
        </div>
        <input
          autoFocus
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="e.g. duplicate of bill #1234"
          className="w-full text-sm border border-rose-300 dark:border-rose-800 rounded px-2 py-1 bg-white dark:bg-gray-900 text-slate-900 dark:text-slate-100"
        />
        <div className="flex justify-end gap-2">
          <Button
            size="small"
            onClick={() => {
              setRejecting(false);
              setRejectReason("");
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || reject.isPending}
            onClick={() => reject.mutate()}
          >
            {reject.isPending ? "Rejecting…" : "Confirm reject"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-4 py-3 flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
          Vendor
          {vendorId && vendorId === defaultVendorId && (
            <span className="ml-1.5 text-blue-600 dark:text-blue-400">✦ AI matched</span>
          )}
        </label>
        <PartySelect
          partyType="vendor"
          value={vendorValue}
          onChange={(p) => {
            setPickedVendor(p);
            setVendorId(p?.id ?? "");
          }}
          label=""
          placeholder="Search vendor…"
        />
        {candidates.length > 1 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500 dark:text-slate-400">Could be:</span>
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setPickedVendor(null);
                  setVendorId(c.id);
                }}
                aria-pressed={vendorId === c.id}
                className={`text-xs rounded-full border px-2 py-0.5 ${
                  vendorId === c.id
                    ? "border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-950 dark:text-blue-200"
                    : "border-slate-300 text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
                }`}
              >
                {c.name}
                {c.code ? <span className="ml-1 font-mono text-slate-400">· {c.code}</span> : null}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[200px]">
        <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
          GL expense account
          {accountId && accountId === suggestedAccount?.id && (
            <span className="ml-1.5 text-blue-600 dark:text-blue-400">✦ AI suggested</span>
          )}
        </label>
        <select
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          className="w-full text-sm border border-slate-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
        >
          <option value="">— pick an account —</option>
          {expenseAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {codeAndName(a)}
            </option>
          ))}
        </select>
      </div>
      {projectId && (
        <>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
              Cost code
            </label>
            <CostCodeSelect
              value={costCode}
              onChange={setCostCode}
              projectId={projectId}
              label=""
              size="small"
              fullWidth
              placeholder="Search cost code…"
            />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">
              Cost type
            </label>
            <select
              value={costTypeId}
              onChange={(e) => setCostTypeId(e.target.value)}
              className="w-full text-sm border border-slate-300 dark:border-gray-700 rounded px-2 py-1 bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
            >
              <option value="">— from cost code —</option>
              {costTypeOptions.map((ct) => (
                <option key={ct.id} value={ct.id}>
                  {ct.name}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      <div className="flex gap-2">
        <Button size="small" onClick={() => setRejecting(true)}>
          Reject
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!vendorId || !accountId || promote.isPending}
          onClick={() => promote.mutate()}
        >
          {promote.isPending
            ? "Promoting…"
            : shownDuplicateIds.length
              ? "Promote anyway → AP invoice"
              : "Promote → AP invoice"}
        </Button>
      </div>
    </div>
  );
}
